import { QueryClient } from '@tanstack/react-query';
import queryKeys from '@/constants/queryKeys';
import { TContent } from '@/types/Content.types';

// A content item can sit in several caches at once — the ranked feed, a filtered or
// search result, the focused loop, the saved list, and its own detail entry. A reaction
// must move all of them together, otherwise navigating between surfaces shows a stale
// like/save state. These helpers patch one item wherever it appears.

// Every list-shaped content cache is keyed by one of these roots.
const CONTENT_LIST_KEYS = [
  queryKeys.content.personalizedFeed,
  queryKeys.content.filtered,
  queryKeys.content.search,
  queryKeys.content.focusedFeed,
  queryKeys.content.feed,
  queryKeys.content.saved,
  queryKeys.content.mine,
  queryKeys.content.author,
];

type TContentPatch = (item: TContent) => TContent;

type TListShape = { data: TContent[] };
type TInfiniteShape = { pages: TListShape[]; pageParams: unknown[] };

const isListShape = (value: unknown): value is TListShape =>
  !!value && typeof value === 'object' && Array.isArray((value as TListShape).data);

const isInfiniteShape = (value: unknown): value is TInfiniteShape =>
  !!value && typeof value === 'object' && Array.isArray((value as TInfiniteShape).pages);

// Returns the same reference when nothing matched, so React Query skips the re-render.
const patchList = (list: TListShape, id: string, patch: TContentPatch): TListShape => {
  let changed = false;
  const data = list.data.map((item) => {
    if (item.id !== id) return item;
    changed = true;
    return patch(item);
  });

  return changed ? { ...list, data } : list;
};

const patchUnknown = (value: unknown, id: string, patch: TContentPatch): unknown => {
  if (isInfiniteShape(value)) {
    let changed = false;
    const pages = value.pages.map((page) => {
      const next = patchList(page, id, patch);
      if (next !== page) changed = true;
      return next;
    });

    return changed ? { ...value, pages } : value;
  }

  if (isListShape(value)) return patchList(value, id, patch);

  return value;
};

// Apply `patch` to the item everywhere it is cached (list surfaces + its detail entry).
export const patchContentInCaches = (queryClient: QueryClient, id: string, patch: TContentPatch): void => {
  CONTENT_LIST_KEYS.forEach((key) => {
    queryClient.setQueriesData({ queryKey: [key] }, (old: unknown) => patchUnknown(old, id, patch));
  });

  queryClient.setQueryData([queryKeys.content.byId, id], (old: TContent | undefined) => (old ? patch(old) : old));
};

const removeFromList = (list: TListShape, id: string): TListShape => {
  const data = list.data.filter((item) => item.id !== id);

  return data.length === list.data.length ? list : { ...list, data };
};

const removeFromUnknown = (value: unknown, id: string): unknown => {
  if (isInfiniteShape(value)) {
    let changed = false;
    const pages = value.pages.map((page) => {
      const next = removeFromList(page, id);
      if (next !== page) changed = true;
      return next;
    });

    return changed ? { ...value, pages } : value;
  }

  if (isListShape(value)) return removeFromList(value, id);

  return value;
};

// Browse surfaces the backend hides not-interested items from.
//
// Deliberately excluded, because the backend does NOT suppress not-interested there:
//  - `saved` — the saved list is not filtered, so a dismissed-but-saved item stays saved.
//  - `feed` — the legacy `GET /content` root (creator-profile content via useContentFeed)
//    does not call the not-interested filter, so an item must not be dropped from it.
const BROWSE_LIST_KEYS = [
  queryKeys.content.personalizedFeed,
  queryKeys.content.filtered,
  queryKeys.content.search,
];

// Optimistically drop an item from the browse-list caches on MARK — the backend hides it
// on the next read; this makes it vanish now without waiting for a refetch.
//
// The focused-feed cache is not mutated here: the user may be marking the very item
// they're viewing in the loop, and removing it mid-stream would shift the index under
// them. It is invalidated instead (see invalidateFocusedFeed) so a re-opened loop is fresh.
export const removeContentFromBrowseCaches = (queryClient: QueryClient, id: string): void => {
  BROWSE_LIST_KEYS.forEach((key) => {
    queryClient.setQueriesData({ queryKey: [key] }, (old: unknown) => removeFromUnknown(old, id));
  });
};

// The focused-feed's peers and cursor replay also suppress not-interested items, so
// its cache goes stale on any mark/unmark. `refetchType: 'none'` marks it stale WITHOUT
// refetching — a currently-open loop keeps its stream (no reshuffle mid-view); the next
// time the loop opens it fetches fresh and reflects the change.
export const invalidateFocusedFeed = (queryClient: QueryClient): void => {
  queryClient.invalidateQueries({ queryKey: [queryKeys.content.focusedFeed], refetchType: 'none' });
};

// On UNMARK (undo), bring the item back into the browse surfaces it was dropped from,
// rather than leaving it missing until the cache goes stale. The deterministic surfaces
// refetch in place (same order, item reappears where it belongs); the ranked personalized
// draw is only marked stale, since force-refetching it would mint a NEW draw and reshuffle
// the whole grid just for one undo.
export const restoreContentToBrowseCaches = (queryClient: QueryClient): void => {
  [queryKeys.content.filtered, queryKeys.content.search].forEach((key) => {
    queryClient.invalidateQueries({ queryKey: [key] });
  });
  queryClient.invalidateQueries({ queryKey: [queryKeys.content.personalizedFeed], refetchType: 'none' });
};

// Counters are server-owned; a local delta can only ever be an estimate, so never let one
// render as negative while the real value is in flight.
const bumped = (count: number | undefined, delta: number): number => Math.max(0, (count ?? 0) + delta);

export const applyLike = (liked: boolean): TContentPatch => (item) => ({
  ...item,
  viewerState: { ...item.viewerState, liked },
  stats: { ...item.stats, likeCount: bumped(item.stats?.likeCount, liked ? 1 : -1) },
});

export const applySave = (saved: boolean): TContentPatch => (item) => ({
  ...item,
  viewerState: { ...item.viewerState, saved },
  stats: { ...item.stats, saveCount: bumped(item.stats?.saveCount, saved ? 1 : -1) },
});

export const applyShare = (): TContentPatch => (item) => ({
  ...item,
  stats: { ...item.stats, shareCount: bumped(item.stats?.shareCount, 1) },
});
